import StationScreen from './StationScreen';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import "./flashStyle.css";
import "./Kitchen.css";
import { toast } from "react-toastify";
import config from '../config';
import { getPollMs } from '../utils/appSettings';
import { useBpFlags } from "../Responsive";
import TableSelector from './TableSelector';
import AssignMeats from './AssignMeats';
import { bbqIcon } from '../utils/bbqTerms';
import { NotesBubble, parsePrefs } from './shared/PreorderNotes';

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);

const stateLabel = (stateName) => {
  const s = String(stateName || '').toLowerCase();

  const map = {
    sitting: tr('Starters'),
    preparing: tr('Appetizers'),
    ontheside: tr('Siders'),
    dessert: tr('Desserts'),
  };

  return map[s] ?? stateName;
};

// JSON στήλες από το kitchen-view (JSON_ARRAYAGG): αντικείμενο ή string ανάλογα με τον driver
const asList = (v) => {
  if (Array.isArray(v)) return v;
  if (!v) return [];
  try { const j = JSON.parse(v); return Array.isArray(j) ? j : []; } catch { return []; }
};

// Κατάσταση BBQ (Pass/Done) ανά κρέας της κράτησης. «Έξυπνα» για πολλά κρέατα: έως 2 φαίνονται
// αναλυτικά, από 3 και πάνω εμφανίζεται σύνοψη «x/n done · y/n pass» που ανοίγει σε λίστα.
const KitchenMeats = ({ meats, expanded, onToggle }) => {
  if (!meats.length) return <span className="km-none">—</span>;
  const done = meats.filter((m) => Number(m.done)).length;
  const pass = meats.filter((m) => Number(m.pass)).length;
  const total = meats.length;
  const chips = meats.map((m) => {
    const d = !!Number(m.done); const pz = !!Number(m.pass);
    return (
      <span key={m.id} className={`km-chip${d ? ' is-done' : ''}${pz ? ' is-pass' : ''}${d && pz ? ' is-ready' : ''}`}
        title={`${m.name} · ${m.grams}g`}>
        <b>{('  ' + m.id).slice(-2)}</b> {m.name}
        <i className={pz ? 'on' : ''}>P</i><i className={d ? 'on' : ''}>D</i>
      </span>
    );
  });
  if (total <= 2) return <div className="km-list">{chips}</div>;
  return (
    <div className="km-list">
      <button type="button" className={`km-summary${done === total && pass === total ? ' is-ready' : ''}`} onClick={onToggle} aria-expanded={expanded}>
        {tr('{{done}}/{{total}} done · {{pass}}/{{total}} pass', { done, pass, total })} {expanded ? '▾' : '▸'}
      </button>
      {expanded && chips}
    </div>
  );
};

const ALARMS_KEY = "estiasync_alarms_on";
const VOICEOVER_KEY = "estiasync_voiceover";

const KitchenView = () => {

  const [reservations, setReservations] = useState([]);
  const [newRecords, setNewRecords] = useState([]);
  const [details] = useState({});   // εφεδρεία μόνο· τα στοιχεία έρχονται με το kitchen-view
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [audio] = useState(new Audio('/alarm.wav'));
  const [playing, setPlaying] = useState(false);
  const [alarmsMuted, setAlarmsMuted] = useState(
    () => localStorage.getItem(ALARMS_KEY) === "1"
  );
  
  // if alarms were ON previously, treat as "used" so notifications can play immediately
  const [alarmUsed, setAlarmUsed] = useState(
    () => localStorage.getItem(ALARMS_KEY) === "1"
  );
  const [notify, setNotify] = useState(false);
  const [pendingActions, setPendingActions] = useState({});
  const [sentActions, setSentActions] = useState({});
  const [expandedMeats, setExpandedMeats] = useState({});
  // Συννεφάκια σημειώσεων: ανοιχτά από προεπιλογή, ό,τι κλείσει ο χρήστης μένει κλειστό στη συσκευή
  const NOTES_KEY = 'kitchen_notes_collapsed_v1';
  const [notesCollapsed, setNotesCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NOTES_KEY) || '{}') || {}; } catch { return {}; }
  });
  const toggleNotes = (k) => setNotesCollapsed((prev) => {
    const next = { ...prev };
    if (next[k]) delete next[k]; else next[k] = 1;
    try { localStorage.setItem(NOTES_KEY, JSON.stringify(next)); } catch { /* χωρίς αποθήκευση */ }
    return next;
  });
  const { isTablet } = useBpFlags();
  const [showSidersSelector, setShowSidersSelector] = useState(false);
  const [activeReservationIds, setActiveReservationIds] = useState([]);
  const [selectorTime, setSelectorTime] = useState(
    () => new Date().toISOString().slice(0, 16)   // YYYY-MM-DDTHH:MM
  );
  const [assignMeatsReservation, setAssignMeatsReservation] = useState(null);
  
  // ---- toast de-duplication (like BBQ) ----
  const STORAGE_KITCHEN_SEEN_NEW = "estiasync_kitchen_seen_new";
  const STORAGE_KITCHEN_SEEN_OVERDUE = "estiasync_kitchen_seen_overdue";
  
  const loadSet = (key) => {
    try {
      const arr = JSON.parse(sessionStorage.getItem(key) || "[]");
      return new Set((arr || []).map(String));
    } catch {
      return new Set();
    }
  };
  
  const saveSet = (key, set) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(Array.from(set)));
    } catch {}
  };
  
  const seenNewRef = useRef(loadSet(STORAGE_KITCHEN_SEEN_NEW));         // "New ..." toasts
  const seenOverdueRef = useRef(loadSet(STORAGE_KITCHEN_SEEN_OVERDUE)); // "Finish ..." toasts

  useEffect(() => {
    return () => {
      saveSet(STORAGE_KITCHEN_SEEN_NEW, seenNewRef.current);
      saveSet(STORAGE_KITCHEN_SEEN_OVERDUE, seenOverdueRef.current);
    };
  }, []);

  const stateDurations = {
      sitting: 2*60, // minutes
      preparing: 12*60, // minutes
      ontheside: 10*60, // minutes
      dessert: 3*60, // minutes
  };

  const toggle = () => {
    const nextOn = !alarmsMuted;
  
    setAlarmUsed(true);
    setAlarmsMuted(nextOn);
    localStorage.setItem(ALARMS_KEY, nextOn ? "1" : "0");
  
    if (nextOn) setPlaying(true);
    else setPlaying(false);
  };

  // --- VoiceOver (English-only) ---
  const [voiceOverEnabled, setVoiceOverEnabled] = useState(
    () => localStorage.getItem(VOICEOVER_KEY) === "1"
  );

  const voiceOverEnabledRef = useRef(voiceOverEnabled);
  useEffect(() => { voiceOverEnabledRef.current = voiceOverEnabled; }, [voiceOverEnabled]);

  const speechQueueRef = useRef([]);
  const speechSpeakingRef = useRef(false);

  const clearSpeechQueue = () => {
    speechQueueRef.current = [];
    speechSpeakingRef.current = false;
    try {
      const synth = window.speechSynthesis;
      if (synth) synth.cancel();
    } catch {}
  };

  const extractToastText = (content) => {
    if (content == null) return "";
    if (typeof content === "function") return extractToastText(content());
    if (typeof content === "string" || typeof content === "number") return String(content);
    if (Array.isArray(content)) return content.map(extractToastText).join(" ");
    if (React.isValidElement(content)) return extractToastText(content.props?.children);
    return "";
  };

  const toKitchenSpeechText = (raw) => {
    let s = String(raw || "").trim();
    if (!s) return "";
  
    // If the toast follows the pattern "12) Something: 12345" → drop ": 12345"
    if (/\)\s*.*:\s*\d+\s*$/.test(s)) {
      s = s.replace(/:\s*\d+\s*$/, "").trim();
    }
  
    // Turn "12) ..." into "12 ..." (remove the ")")
    s = s.replace(/^\s*(\d+)\)\s*/, "$1 ").trim();
  
    return s;
  };

  const pickEnglishVoice = (synth) => {
    try {
      const voices = typeof synth.getVoices === "function" ? synth.getVoices() : [];
      return voices.find(v => String(v.lang || "").toLowerCase().startsWith("en")) || null;
    } catch {
      return null;
    }
  };

  const speakNextFromQueue = () => {
    if (!voiceOverEnabledRef.current) return;
    if (speechSpeakingRef.current) return;

    const synth = window.speechSynthesis;
    if (!synth || !window.SpeechSynthesisUtterance) return;

    const next = speechQueueRef.current.shift();
    if (!next) return;

    speechSpeakingRef.current = true;

    const u = new SpeechSynthesisUtterance(next.text);
    u.lang = "en-US"; // ALWAYS English

    const v = pickEnglishVoice(synth);
    if (v) u.voice = v;

    u.onend = () => {
      speechSpeakingRef.current = false;
      speakNextFromQueue();
    };

    u.onerror = () => {
      speechSpeakingRef.current = false;
      speakNextFromQueue();
    };

    try { synth.resume?.(); } catch {}
    synth.speak(u);
  };

  const enqueueSpeech = (text) => {
    const msg = String(text || "").trim();
    if (!msg) return;
    speechQueueRef.current.push({ text: msg });
    speakNextFromQueue();
  };

  const toggleVoiceOver = () => {
    const next = !voiceOverEnabled;

    if (!next) clearSpeechQueue();

    // Don’t enable if unsupported
    if (next && (!window.speechSynthesis || !window.SpeechSynthesisUtterance)) {
      toast.info("VoiceOver is not supported in this browser.");
      return;
    }

    voiceOverEnabledRef.current = next;

    setVoiceOverEnabled(next);
    localStorage.setItem(VOICEOVER_KEY, next ? "1" : "0");

    if (next) {
      // speak immediately on enable
      enqueueSpeech("VoiceOver enabled");
    }
  };

  const toastOrigRef = useRef(null);
  const toastWrappedRef = useRef(false);

  useEffect(() => {
    if (!voiceOverEnabled) {
      if (toastWrappedRef.current && toastOrigRef.current) {
        const o = toastOrigRef.current;
        toast.success = o.success;
        toast.error   = o.error;
        toast.warn    = o.warn;
        toast.info    = o.info;
        if (o.dark) toast.dark = o.dark;
        toastWrappedRef.current = false;
        clearSpeechQueue();
      }
      return;
    }

    if (toastWrappedRef.current) return;

    toastOrigRef.current = {
      success: toast.success,
      error: toast.error,
      warn: toast.warn,
      info: toast.info,
      dark: toast.dark,
    };

    const wrap = (fn) => (content, opts) => {
      const id = fn(content, opts);
      const raw = extractToastText(content);
      const spoken = toKitchenSpeechText(raw);
      enqueueSpeech(spoken);
      return id;
    };

    toast.success = wrap(toastOrigRef.current.success);
    toast.error   = wrap(toastOrigRef.current.error);
    toast.warn    = wrap(toastOrigRef.current.warn);
    toast.info    = wrap(toastOrigRef.current.info);
    if (toastOrigRef.current.dark) toast.dark = wrap(toastOrigRef.current.dark);

    toastWrappedRef.current = true;

    return () => {
      if (toastWrappedRef.current && toastOrigRef.current) {
        const o = toastOrigRef.current;
        toast.success = o.success;
        toast.error   = o.error;
        toast.warn    = o.warn;
        toast.info    = o.info;
        if (o.dark) toast.dark = o.dark;
        toastWrappedRef.current = false;
      }
    };
  }, [voiceOverEnabled]);


  useEffect(() => {
      playing ? audio.play() : audio.pause();
    },
    [playing]
  );

  useEffect(() => {
    audio.addEventListener('ended', () => setPlaying(false));
    return () => {
      audio.removeEventListener('ended', () => setPlaying(false));
    };
  }, []);

  useEffect(() => {
    if(reservations && !reservations.length)
      setMessage('');
  }, [reservations]);

  // Open AssignMeats modal for a reservation (from any place: table selector OR row icon)
  const openAssignMeatsForReservationId = (reservationId) => {
    const id = Number(reservationId);
    if (!id) return;
  
    // If you ever add richer reservation info in details, this will pick it up.
    const reservation = details[id]?.reservation || { id };
  
    setAssignMeatsReservation(reservation);
  };


  const fetchReservations = async () => {
    try {
        const response = await axios.get(tr('{{API_BASE_URL}}/assignedstates/kitchen-view', {
          API_BASE_URL: config.API_BASE_URL
        }));
        const newReservations = response.data;
        var notify = false;    

        newReservations.forEach(r => {
          const idStr = String(r.assignedstate_id);
          const stateKey = String(r.state_name || "").toLowerCase();
          const limitSec = stateDurations[stateKey];
        
          // 1) Overdue "Finish ..." toast ONCE per assignedstate_id (even across tab changes)
          if (
            limitSec != null &&
            r.seconds_passed >= limitSec &&
            !seenOverdueRef.current.has(idStr)
          ) {
            seenOverdueRef.current.add(idStr);
            notify = true;
        
            toast.error(tr('{{var1}}) Finish {{state_name}}: {{reservation_id}}', {
              var1: ('  ' + r.assignedstate_id).slice(-2),
              state_name: stateLabel(r.state_name),
              reservation_id: r.reservation_id
            }));
          }
        
          // 2) (11/2026) τραπέζια/πελάτης/σχόλια έρχονται πλέον μέσα στο kitchen-view — χωρίς
          //    ξεχωριστό request ανά γραμμή σε κάθε poll, και πάντα φρέσκα.
        });


        // Fetch assigned states that have been completed but their corresponding action is not yet completed
        const assignedStatesResponse = await axios.get(tr('{{API_BASE_URL}}/assignedstates/kitchen-view/pending-actions', {
          API_BASE_URL: config.API_BASE_URL
        }));
        
        if (assignedStatesResponse.data.length > 0) {
            let sent = {};
            assignedStatesResponse.data.forEach(item => {
                sent[item.assignedstate_id] = true;
            });
            //console.log('sent',sent)
            setSentActions(sent);
        }

        setNotify(notify);

        // Detect truly new items using a persistent "seen" set (survives tab changes)
        const actuallyNew = [];
        for (const r of newReservations) {
          const idStr = String(r.assignedstate_id);
          if (!seenNewRef.current.has(idStr)) {
            seenNewRef.current.add(idStr);
            actuallyNew.push(r);
          }
        }
        
        if (actuallyNew.length) {
          setNewRecords(actuallyNew); // keep your existing effect (below) if you want
        }
        
        setReservations(newReservations);
        
    } catch (err) {
        console.error('Error fetching kitchen view:', err);
        setError('Failed to fetch kitchen view data');
    }
  };

  useEffect(() => {
      if (alarmUsed && alarmsMuted && notify) 
        setPlaying(true);
  }, [notify]);

  useEffect(() => {
    if (!newRecords.length) return;
  
    newRecords.forEach(r => {
      toast.warn(tr('{{var1}}) New {{state_name}}: {{reservation_id}}', {
        var1: ('  ' + r.assignedstate_id).slice(-2),
        state_name: stateLabel(r.state_name),
        reservation_id: r.reservation_id
      }));
    });
  
    if (alarmUsed && alarmsMuted) setPlaying(true);
  
    // important: clear so it never replays from the same array instance
    setNewRecords([]);
  }, [newRecords, alarmUsed, alarmsMuted]);

  useEffect(() => {
      fetchReservations();

      // Set up interval to fetch data every 1 second
      const interval = setInterval(fetchReservations, getPollMs('fast'));

      return () => clearInterval(interval); // Cleanup interval on unmount
  }, []);

  const calculateBarWidth = (stateName, minutesPassed) => {
      const totalDuration = stateDurations[stateName.toLowerCase()];
      if (!totalDuration) return 0;

      const elapsedTime = Math.min(minutesPassed, totalDuration);
      return (elapsedTime / totalDuration) * 100;
  };

  const calculateBarColor = (stateName, minutesPassed) => {
      const totalDuration = stateDurations[stateName.toLowerCase()];
      if (!totalDuration) return 'gray';

      const elapsedTime = Math.min(minutesPassed, totalDuration);
      return elapsedTime >= totalDuration ? 'darkRed' : '#4caf50';
  };

  const handleCompleteAction = (assignedstate_id) => {
      if (pendingActions[assignedstate_id]) return;

      setPendingActions((prev) => ({ ...prev, [assignedstate_id]: true }));

      const timer = setTimeout(() => {
        axios.post(tr('{{API_BASE_URL}}/assignedstates/kitchen-view/ready', {
          API_BASE_URL: config.API_BASE_URL
        }), { assignedstate_id })
            .then(({ data }) => {
          if (data?.createdAction) {
            setSentActions(prev => ({ ...prev, [assignedstate_id]: true }));
            setMessage(tr('Service notified for state #{{assignedstate_id}}', {
              assignedstate_id
            }));
          } else {
            setMessage(tr('State #{{assignedstate_id}} is ready (no service action needed)', {
              assignedstate_id
            }));
          }
        })
            .catch((err) => {
          console.error('Failed to create a service action:', err);
          setError(tr('Failed to create a service action for state: {{var1}}', {
            var1: ('  '+assignedstate_id).slice(-2)
          }));
        })
            .finally(() => {
                setPendingActions((prev) => {
                    const updated = { ...prev };
                    delete updated[assignedstate_id];
                    return updated;
                });
            });
      }, 6000);

      setPendingActions((prev) => ({ ...prev, [assignedstate_id]: timer }));
  };

  const cancelCompleteAction = (assignedstate_id) => {
      if (pendingActions[assignedstate_id]) {
          clearTimeout(pendingActions[assignedstate_id]);

          setPendingActions((prev) => {
              const updated = { ...prev };
              delete updated[assignedstate_id];
              return updated;
          });
      }
  };

  const removeAction = (assignedstate_id) => {
    axios.post(tr('{{API_BASE_URL}}/assignedstates/kitchen-view/complete', {
      API_BASE_URL: config.API_BASE_URL
    }), { assignedstate_id })
        .then(() => {
      let sent = sentActions;
      delete sent[assignedstate_id];
      setSentActions(sent);
      setReservations((prev) =>
          prev.filter((item) => item.assignedstate_id !== assignedstate_id)
      );
      setMessage(tr('State {{var1}} marked as completed', {
        var1: ('  '+assignedstate_id).slice(-2)
      }));
    })
        .catch((err) => {
      console.error('Error marking state as completed:', err);
      setError(tr('Failed to mark state {{var1}} as completed', {
        var1: ('  '+assignedstate_id).slice(-2)
      }));
    })
  };

  // --- "Add Siders" flow ---

  // 1) Open TableSelector for active reservations (incomplete + has sitting)
  const openAddSiders = async () => {
    try {
      const { data } = await axios.get(
        tr('{{API_BASE_URL}}/assignedstates/active-sitting-reservations', {
          API_BASE_URL: config.API_BASE_URL
        })
      );

      const ids = Array.from(
        new Set(
          (data || [])
            .map(row => Number(row.reservation_id))
            .filter(Boolean)
        )
      );

      if (!ids.length) {
        toast.info('No active sitting reservations right now.');
        return;
      }

      setActiveReservationIds(ids);
      setSelectorTime(new Date().toISOString().slice(0, 16));
      setShowSidersSelector(true);
    } catch (err) {
      console.error('Error preparing Add Siders:', err);
      toast.error('Failed to load active reservations for siders.');
    }
  };

  const closeSidersSelector = () => {
    setShowSidersSelector(false);
  };

  // 2) When user clicks a table in the selector
  const handleTablePickedForSiders = (table) => {
    const reservationId = Number(table?.reserved_id);
    if (!reservationId) return;

    // Reuse reservation details if we already fetched them
    const reservation =
      details[reservationId]?.reservation || { id: reservationId };

    setAssignMeatsReservation(reservation);
    setShowSidersSelector(false);
  };

  // 3) Close AssignMeats modal
  const handleCloseAssignMeats = () => {
    setAssignMeatsReservation(null);
  };


  if (error) {
      return <div className="error">{error}</div>;
  }

  const isLate = (r) => {
    const d = stateDurations[String(r.state_name || '').toLowerCase()];
    return d != null && Number(r.seconds_passed) >= d;
  };
  const lateCount = reservations.filter(isLate).length;
  const fmtTimer = (sec) => {
    const s2 = Math.max(0, Number(sec) || 0);
    return `${Math.floor(s2 / 60)}:${String(s2 % 60).padStart(2, '0')}`;
  };

  return (
    <>
    <div className={`kv${isTablet ? ' tablet-scroll' : ''}`}>
      <header className="kv-header">
        <div className="kv-head-left">
          <h2 className="kv-title">{tr('Kitchen View')}</h2>
          <span className="kv-count">{reservations.length} {tr('tasks')}</span>
          {lateCount > 0 && <span className="kv-count is-late">⏱ {lateCount} {tr('late')}</span>}
        </div>
        <div className="kv-head-right">
          <button className="kv-btn kv-btn--primary" onClick={openAddSiders}>
            <span aria-hidden="true">🍽</span> {tr('Add Siders')}
          </button>
          <button className={`kv-toggle${alarmsMuted ? ' is-on' : ''}`} onClick={toggle} aria-pressed={alarmsMuted}
            title={alarmsMuted ? tr('Alarms are on') : tr('Alarms are off')}>
            <span className="kv-dot" /> {alarmsMuted ? '🔔' : '🔕'} {tr('Alarms')}
          </button>
          <button className={`kv-toggle${voiceOverEnabled ? ' is-on' : ''}`} onClick={toggleVoiceOver} aria-pressed={voiceOverEnabled}
            title={voiceOverEnabled ? tr('VoiceOver is on') : tr('VoiceOver is off')}>
            <span className="kv-dot" /> {voiceOverEnabled ? '🔊' : '🔇'} {tr('VoiceOver')}
          </button>
        </div>
      </header>

      {message && <div className="kv-flash">{message}</div>}

      <StationScreen station="kitchen" embedded />

      {reservations.length === 0 ? (
          <div className="kv-empty">✓ {tr('No more tasks found yet...')}</div>
      ) : (
          <div className="kv-list">
            {reservations.map((item) => {
              const barWidth = calculateBarWidth(item.state_name, item.seconds_passed);
              const late = isLate(item);
              const barColor = calculateBarColor(item.state_name, item.seconds_passed) === 'gray'
                ? '#9ca3af' : late ? '#dc2626' : barWidth >= 70 ? '#f59e0b' : '#22c55e';

              // τραπέζια/πελάτης από το kitchen-view (φρέσκα σε κάθε poll)
              const tables = item.tables ?? details[item.reservation_id]?.tables ?? '';
              const clientName = `${item.client_lastname ?? details[item.reservation_id]?.client?.lastname ?? ''} ${item.client_firstname ?? details[item.reservation_id]?.client?.firstname ?? ''}`.trim();
              const meats = asList(item.meats);

              // Σημειώσεις: ΜΟΝΟ γενικές — πελάτη / κράτησης / προπαραγγελίας / ανοιχτής παραγγελίας
              const noteLines = [
                { icon: '👤', label: tr('Client'), text: item.client_comment },
                { icon: '📝', label: tr('Reservation'), text: item.reservation_comment },
                { icon: '🍖', label: tr('Pre-order'), text: item.preorder_comment },
                { icon: '🧾', label: tr('Order comment'), text: item.order_comment },
              ];
              const notePrefs = [...new Set([...parsePrefs(item.preorder_prefs), ...parsePrefs(item.order_prefs)])];
              const hasNotes = notePrefs.length > 0 || noteLines.some((l) => String(l.text || '').trim());
              const notesKey = `r${item.reservation_id}`;
              const notesClosed = !!notesCollapsed[notesKey];
              const canSiders = ['sitting', 'preparing'].includes(String(item.state_name || '').toLowerCase());
              const pending = !!pendingActions[item.assignedstate_id];
              const sent = !!sentActions[item.assignedstate_id];

              return (
                <article key={item.assignedstate_id} className={`kv-card${late ? ' is-late' : ''}`}
                  style={{ '--state': item.state_color || '#9ca3af' }}>
                  <div className="kv-row">
                    <div className="kv-id" title={tr('ID')}>
                      <span className="kv-id-num">{('  ' + item.assignedstate_id).slice(-2)}</span>
                      {item.order ? <span className="kv-order">#{item.order}</span> : null}
                    </div>

                    <div className="kv-who">
                      <div className={`kv-table${tables ? '' : ' is-none'}`}>
                        {tables ? <><small>{tr('Table')}</small>{tables}</> : tr('No tables')}
                      </div>
                      <div className="kv-meta">
                        {clientName && <span className="kv-client">{clientName}</span>}
                        <span>👥 {item.people}</span>
                        <span className="kv-muted">#{item.reservation_id}</span>
                        {hasNotes && notesClosed && (
                          <NotesBubble lines={noteLines} prefs={notePrefs} collapsed onToggle={() => toggleNotes(notesKey)} />
                        )}
                      </div>
                    </div>

                    <span className="kv-state">{stateLabel(item.state_name)}</span>

                    <div className="kv-bbq">
                      <KitchenMeats meats={meats} expanded={!!expandedMeats[item.reservation_id]}
                        onToggle={() => setExpandedMeats((p) => ({ ...p, [item.reservation_id]: !p[item.reservation_id] }))} />
                    </div>

                    <div className="kv-timer">
                      <span className="kv-time">{fmtTimer(item.seconds_passed)}<small>{tr('min')}</small></span>
                      <span className="kv-bar"><span style={{ width: `${barWidth}%`, background: barColor }} /></span>
                    </div>

                    <div className="kv-actions">
                      {canSiders && (
                        <button className="kv-icon-btn" title={tr('Assign / edit siders')}
                          onClick={() => openAssignMeatsForReservationId(item.reservation_id)}>
                          {bbqIcon()}
                        </button>
                      )}
                      {sent ? (
                        <button className="kv-done is-sent" title={tr('Served')} onClick={() => removeAction(item.assignedstate_id)}>
                          🍽 {tr('Served')}
                        </button>
                      ) : pending ? (
                        <button className="kv-done is-pending" title={tr('Cancel')} onClick={() => cancelCompleteAction(item.assignedstate_id)}>
                          <span className="progress-container">
                            <svg className="progress-circle" width="22" height="22" viewBox="0 0 36 36"><circle cx="18" cy="18" r="16"></circle></svg>
                            <span className="progress-x">{"\u2716"}</span>
                          </span>
                          {tr('Cancel')}
                        </button>
                      ) : (
                        <button className="kv-done" title={tr('Ready')} onClick={() => handleCompleteAction(item.assignedstate_id)}>
                          ✓ {tr('Ready')}
                        </button>
                      )}
                    </div>
                  </div>

                  {hasNotes && !notesClosed && (
                    <div className="kv-notes">
                      <NotesBubble lines={noteLines} prefs={notePrefs} collapsed={false} onToggle={() => toggleNotes(notesKey)} />
                    </div>
                  )}
                </article>
              );
            })}
          </div>
      )}
      {/* --- Modal: TableSelector for Add Siders --- */}
      {showSidersSelector && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={closeSidersSelector}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 16,
              maxWidth: isTablet ? '95%' : '80%',
              width: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>{tr('Select table (active reservations)')}</h3>
              <button
                onClick={closeSidersSelector}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: 18,
                  cursor: 'pointer',
                }}
              >{tr('✕')}</button>
            </div>

            <TableSelector
              reservationTime={selectorTime}
              selectedAssignedTables={[]}
              onTablesSelected={() => {}}
              areaFilter={null}
              tableFilter={(t) =>
                t.reserved_id &&
                activeReservationIds.includes(Number(t.reserved_id))
              }
              onAreaTabChange={() => {}}
              service_view
              people={0}
              activeReservationId={null}
              onTableClick={handleTablePickedForSiders}
            />

            <div
              style={{
                textAlign: 'right',
                marginTop: 8,
                fontSize: 12,
                color: '#666',
              }}
            >{tr('Click a table to assign / edit siders for its reservation.')}</div>
          </div>
        </div>
      )}
      {/* --- Modal: AssignMeats for selected reservation --- */}
      {assignMeatsReservation && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2100,
          }}
          onClick={handleCloseAssignMeats}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 10,
              maxWidth: '96%',
              width: isTablet ? '95%' : '1100px',
              maxHeight: '95vh',
              overflow: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <AssignMeats
              reservation={assignMeatsReservation}
              onCloseModal={handleCloseAssignMeats}
              onBeforeSave={null}
            />
          </div>
        </div>
      )}
    </div>
  </>
  );
};

export default KitchenView;
